import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  LayoutDashboard,
  Users,
  User,
  UserCog,
  Building2,
  FileText,
  BarChart3,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Settings,
  HelpCircle,
  LogOut,
  BotMessageSquare,
} from "lucide-react";
import { canUseAgent } from "@/auth/capabilities";
import { AppDispatch } from "@/store/store";
import { ROLE_LABELS } from "@/types/auth";
import { logout } from "@/store/slices/authSlice";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import queryClient from "@/queryClient";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import TrialBadge from "@/components/trial/TrialBadge";
import { canAccessSettings } from "@/pages/broker/settingsSections";
import { USER_PAGES } from "@/pages/admin/users/userPages";
import { MANAGED_USER_ROLES, ManagedUserRole } from "@/services/users";
import { cn } from "@/lib/utils";

interface SidebarProps {
  drawerWidth: number;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
  collapsed?: boolean;
  onCollapseToggle?: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

/** A labelled, always-open set of nav items; collapsed, only its items show. */
interface NavGroup {
  label: string;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

const USER_PAGE_ICONS: Record<ManagedUserRole, React.ReactNode> = {
  broker: <Users className="h-5 w-5" />,
  underwriter: <User className="h-5 w-5" />,
  admin_support: <UserCog className="h-5 w-5" />,
};

function SidebarNavItem({
  item,
  collapsed,
  isActive,
  indent = false,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  isActive: boolean;
  indent?: boolean;
  onClick: () => void;
}) {
  const button = (
    <button
      onClick={onClick}
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg text-sm font-medium",
        "transition-all duration-150 ease-spring",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 focus-visible:ring-offset-white",
        "active:scale-[0.97]",
        collapsed ? "h-10 justify-center px-2" : cn("h-10 pr-3", indent ? "pl-6" : "pl-3"),
        isActive
          ? "bg-primary/10 font-semibold text-primary"
          : "text-muted-foreground hover:bg-primary/5 hover:text-primary"
      )}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">{item.icon}</span>
      {!collapsed && <span>{item.label}</span>}
    </button>
  );

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

function NavInner({
  navItems,
  collapsed,
  onCollapseToggle,
  onItemClick,
}: {
  navItems: NavEntry[];
  collapsed: boolean;
  onCollapseToggle?: () => void;
  onItemClick?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isOrgManager, portalName } = useCurrentUser();

  const handleLogout = async () => {
    await dispatch(logout());
    queryClient.clear();
    navigate("/login");
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onItemClick?.();
  };

  const renderItem = (item: NavItem, indent = false) => (
    <SidebarNavItem
      key={item.path}
      item={item}
      collapsed={collapsed}
      isActive={
        location.pathname === item.path ||
        (item.path !== "/dashboard" && location.pathname.startsWith(item.path))
      }
      indent={indent}
      onClick={() => handleNavigate(item.path)}
    />
  );

  const roleLabel = ROLE_LABELS[user?.role ?? "broker"];
  const initials = user?.email?.charAt(0).toUpperCase() ?? "U";

  const accountNavItems: NavItem[] = [
    ...(user?.billing_enabled
      ? [{ label: "Billing", path: "/billing", icon: <CreditCard className="h-5 w-5" /> }]
      : []),
    ...(canAccessSettings({ isOrgManager })
      ? [{ label: "Settings", path: "/setting", icon: <Settings className="h-5 w-5" /> }]
      : []),
    { label: "Help", path: "/help", icon: <HelpCircle className="h-5 w-5" /> },
  ];
  const onProfile = location.pathname.startsWith("/profile");
  const profileLabel = ["Profile", portalName, user?.email].filter(Boolean).join(", ");
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary";

  const logoutButton = (
    <button
      type="button"
      aria-label="Logout"
      onClick={() => {
        void handleLogout();
      }}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg text-sm font-medium text-destructive",
        "transition-all duration-150 ease-spring",
        "hover:bg-destructive/5 active:scale-[0.97]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40 focus-visible:ring-offset-1",
        collapsed ? "h-10 justify-center px-2" : "h-10 px-3"
      )}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        <LogOut className="h-5 w-5" />
      </span>
      {!collapsed && <span>Logout</span>}
    </button>
  );

  return (
    <div
      className="flex h-full flex-col bg-white/90 backdrop-blur-xl"
      onClick={(e) => {
        // Toggle collapse when clicking any non-interactive whitespace in the sidebar
        const target = e.target as HTMLElement;
        if (!target.closest('button, a, input, [role="button"]')) {
          onCollapseToggle?.();
        }
      }}
    >
      {/* Brand */}
      <div
        className={cn(
          "flex h-16 shrink-0 items-center border-b border-indigo-100/80",
          collapsed ? "justify-center px-2" : "gap-3 px-4"
        )}
      >
        <img src="/steddy-star.svg" alt="Steddy AI" className="h-8 w-8 shrink-0" />
        {!collapsed && (
          <div className="min-w-0">
            <div className="truncate text-[15px] font-bold tracking-tight text-foreground">
              Steddy AI
            </div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              Intelligent MCA Platform
            </div>
          </div>
        )}
      </div>

      {/* Nav items */}
      <nav
        className={cn(
          "dark-scroll flex flex-1 flex-col gap-0.5 overflow-y-auto py-3",
          collapsed ? "px-2" : "px-3"
        )}
      >
        {navItems.map((entry) =>
          "items" in entry ? (
            <div key={entry.label} role="group" aria-label={entry.label} className="mt-3">
              {!collapsed && (
                <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {entry.label}
                </div>
              )}
              <div className="flex flex-col gap-0.5">
                {entry.items.map((item) => renderItem(item, true))}
              </div>
            </div>
          ) : (
            renderItem(entry)
          )
        )}
      </nav>

      {/* Account links + user card (opens the profile) — always visible */}
      <div className="shrink-0 border-t border-indigo-100/80">
        <div className={cn("flex flex-col gap-0.5 py-2", collapsed ? "px-2" : "px-3")}>
          {accountNavItems.map((item) => {
            const isActive =
              location.pathname === item.path || location.pathname.startsWith(item.path);
            return (
              <SidebarNavItem
                key={item.path}
                item={item}
                collapsed={collapsed}
                isActive={isActive}
                onClick={() => handleNavigate(item.path)}
              />
            );
          })}
          {collapsed ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>{logoutButton}</TooltipTrigger>
                <TooltipContent side="right" className="font-medium">
                  Logout
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            logoutButton
          )}
        </div>

        {collapsed ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => handleNavigate("/profile")}
                  aria-label={profileLabel}
                  aria-current={onProfile ? "page" : undefined}
                  className={cn(
                    "flex w-full items-center justify-center border-t border-indigo-100/80 py-3 transition-colors hover:bg-primary/5",
                    focusRing,
                    onProfile && "bg-primary/5"
                  )}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                    {initials}
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">
                <p className="font-semibold">
                  {portalName}
                  {user?.is_trial_account && " (Trial)"}
                </p>
                <p>{user?.email}</p>
                <p className="opacity-80">{roleLabel}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <button
            type="button"
            onClick={() => handleNavigate("/profile")}
            aria-label={profileLabel}
            aria-current={onProfile ? "page" : undefined}
            className={cn(
              "flex w-full items-center gap-3 border-t border-indigo-100/80 px-4 py-3 text-left transition-colors hover:bg-primary/5",
              focusRing,
              onProfile && "bg-primary/5"
            )}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                <span className="truncate">{portalName}</span>
                {user?.is_trial_account && <TrialBadge />}
              </div>
              <div className="truncate text-[11px] text-muted-foreground">{user?.email}</div>
              <div className="text-[10px] text-muted-foreground/60">{roleLabel}</div>
            </div>
          </button>
        )}

        {/* Collapse toggle */}
        <div className="border-t border-indigo-100/80 p-2">
          <button
            type="button"
            onClick={onCollapseToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "flex w-full items-center rounded-lg px-2 py-2 text-muted-foreground transition-colors",
              "hover:bg-primary/5 hover:text-primary active:scale-[0.97]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              collapsed ? "justify-center" : "justify-between"
            )}
          >
            {!collapsed && (
              <span className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground/60">
                © {new Date().getFullYear()} Steddy
              </span>
            )}
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

const Sidebar: React.FC<SidebarProps> = ({
  drawerWidth,
  mobileOpen,
  onMobileOpenChange,
  collapsed = false,
  onCollapseToggle,
}) => {
  const { user, isOrgManager, isFunderStaff, userRole } = useCurrentUser();

  const brokerNavItems: NavItem[] = [
    { label: "Dashboard", path: "/dashboard", icon: <LayoutDashboard className="h-5 w-5" /> },
    { label: "My Applications", path: "/applications", icon: <BarChart3 className="h-5 w-5" /> },
  ];

  const orgManagerNavItems: NavEntry[] = [
    { label: "Dashboard", path: "/dashboard", icon: <LayoutDashboard className="h-5 w-5" /> },
    { label: "Applications", path: "/applications", icon: <Building2 className="h-5 w-5" /> },
    // One tab, no nesting. If the signed-documents list lands later, nest it here:
    // { label: "Documents", path: "/documents", icon: <FileText className="h-5 w-5" />,
    //   children: [
    //     { label: "Templates", path: "/documents" },
    //     { label: "Signed", path: "/documents/signed" },
    //   ] },
    { label: "Documents", path: "/documents", icon: <FileText className="h-5 w-5" /> },
    {
      label: "Users",
      items: MANAGED_USER_ROLES.map((role) => ({
        label: USER_PAGES[role].title,
        path: USER_PAGES[role].path,
        icon: USER_PAGE_ICONS[role],
      })),
    },
  ];

  const underwriterNavItems: NavItem[] = [
    { label: "Dashboard", path: "/dashboard", icon: <LayoutDashboard className="h-5 w-5" /> },
    { label: "Applications", path: "/applications", icon: <Building2 className="h-5 w-5" /> },
  ];

  const roleNavItems = isOrgManager
    ? orgManagerNavItems
    : userRole === "underwriter" || isFunderStaff
      ? underwriterNavItems
      : brokerNavItems;
  const navItems = canUseAgent(user)
    ? [
        ...roleNavItems,
        {
          label: "Assistant",
          path: "/assistant",
          icon: <BotMessageSquare className="h-5 w-5" />,
        },
      ]
    : roleNavItems;

  const navProps = { navItems, collapsed, onCollapseToggle };

  return (
    <>
      {/* Mobile sheet */}
      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          side="left"
          className="w-[min(280px,85vw)] border-r border-indigo-100/50 bg-white/95 p-0 backdrop-blur-xl [&>button]:text-muted-foreground [&>button]:hover:text-foreground"
        >
          <NavInner {...navProps} collapsed={false} onItemClick={() => onMobileOpenChange(false)} />
        </SheetContent>
      </Sheet>

      {/* Desktop fixed sidebar */}
      <aside
        className="fixed left-0 top-[var(--env-ribbon-height)] z-30 hidden h-screen border-r border-indigo-100/80 bg-white/90 backdrop-blur-xl sm:block"
        style={{ width: drawerWidth }}
      >
        <NavInner {...navProps} />
      </aside>
    </>
  );
};

export default Sidebar;
